import React, { useMemo } from "react";
import { Layers, Shapes, Target } from "lucide-react";
import { Skeleton } from "../../../ui/skeleton";
import { EmptyState, InsightCard, InsightPill, SectionHeading } from "../components/Primitives";
import { BarList, ColumnChart } from "../components/DataBars";
import { StakeMix } from "../components/StakeMix";
import { BET_TYPE_LABELS, count, humanise, pct } from "../utils/format";
import { deriveFavourites, hasCasinoProfile, hasSportsActivity } from "../utils/derive";

/**
 * Section D — Sports DNA (§4.5). Odds appetite, bet-type shape, stake mix and
 * favourites.
 *
 * The integers under by_sport -> ... -> markets are relative bet counts, not
 * money (§2.3), so they are drawn as weights and labelled "bets".
 */

/** Reads the odds distribution and names its shape. */
function oddsInsight(ranges) {
  if (!ranges || !ranges.length) return null;

  const shortOdds = ranges
    .filter((range) => upperBound(range.range) <= 2.0)
    .reduce((sum, range) => sum + (range.pct || 0), 0);
  const longshots = ranges
    .filter((range) => lowerBound(range.range) >= 50)
    .reduce((sum, range) => sum + (range.pct || 0), 0);

  if (shortOdds >= 50 && longshots >= 10) {
    return `${pct(shortOdds)} of bets at odds 2.0 or below, plus ${pct(longshots)} at 50+ — bankers with a jackpot tail.`;
  }
  if (shortOdds >= 50) return `${pct(shortOdds)} of bets sit at odds 2.0 or below — a short-odds player.`;
  if (longshots >= 20) return `${pct(longshots)} of bets are at odds of 50 or more — a longshot player.`;
  return null;
}

/** Reads the accumulator mix and names its shape. */
function betTypeInsight(types) {
  if (!types || !types.length) return null;
  const big = types.find((type) => type.type === "10fold_plus");
  if (big && big.pct >= 20) {
    return `1 in ${Math.round(100 / big.pct)} bets is a 10-leg-plus accumulator.`;
  }
  const singles = types.find((type) => type.type === "single");
  if (singles && singles.pct >= 60) return `Mostly singles — ${pct(singles.pct)} of bets are one-leg.`;
  return null;
}

const lowerBound = (range) => Number(String(range).replace("+", "").split("-")[0]) || 0;
const upperBound = (range) => {
  const parts = String(range).split("-");
  if (parts.length < 2) return Infinity;
  return Number(parts[1]) || Infinity;
};

function OddsAppetite({ ranges }) {
  if (!ranges || !ranges.length) return <EmptyState minHeight={168} message="No settled sports bets yet." />;

  const rows = ranges.map((range) => ({
    label: range.range,
    value: range.pct,
    display: pct(range.pct),
    tip: `Odds ${range.range} · ${pct(range.pct)} of bets`,
  }));

  const insight = oddsInsight(ranges);

  return (
    <div className="space-y-3">
      <ColumnChart rows={rows} height={128} colorClass="bg-chart-sports" />
      {insight && (
        <InsightPill icon={Target} tone="accent">
          {insight}
        </InsightPill>
      )}
    </div>
  );
}

function BetTypes({ types }) {
  if (!types || !types.length) return <EmptyState minHeight={168} message="No settled sports bets yet." />;

  // Zero buckets keep their slot rather than disappearing — the gap between
  // 3-folds and 10-folds is itself the story here.
  const rows = types.map((type) => ({
    label: BET_TYPE_LABELS[type.type] || humanise(type.type),
    value: type.pct,
    display: pct(type.pct),
    tip: `${BET_TYPE_LABELS[type.type] || humanise(type.type)} · ${pct(type.pct)} of bets`,
  }));

  const insight = betTypeInsight(types);

  return (
    <div className="space-y-3">
      <BarList
        rows={rows}
        max={100}
        colorClass="bg-chart-sports"
        labelWidth={58}
        valueWidth={36}
        emptySlotHint="0%"
      />
      {insight && (
        <InsightPill icon={Layers} tone="accent">
          {insight}
        </InsightPill>
      )}
    </div>
  );
}

function ChipRow({ label, items }) {
  if (!items || !items.length) return null;
  return (
    <div>
      <div className="mb-1.5 text-xs text-muted-foreground">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <span
            key={item}
            className="inline-flex items-center rounded-md border bg-muted px-2 py-1 text-xs text-foreground"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function Favourites({ favourites, summary }) {
  const league = favourites.find((entry) => entry.teams.length);

  if (!league && !(summary?.top_5_leagues || []).length) {
    return <EmptyState minHeight={168} message="No favourite teams or leagues yet." />;
  }

  const maxWeight = league
    ? Math.max(...league.teams.flatMap((team) => team.markets.map((market) => market.weight)), 1)
    : 1;

  return (
    <div className="space-y-4">
      {league && (
        <div>
          <div className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <span>{humanise(league.sport)}</span>
            <span aria-hidden="true">→</span>
            <span className="font-medium text-foreground">{humanise(league.league)}</span>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[...league.teams]
              .sort((a, b) => b.totalBets - a.totalBets)
              .map((team) => (
                <div key={team.team} className="rounded-md border bg-muted p-3">
                  <div className="mb-2 flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-foreground">{humanise(team.team)}</span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {count(team.totalBets)} bets
                    </span>
                  </div>
                  <BarList
                    rows={team.markets.map((market) => ({
                      label: humanise(market.market),
                      value: market.weight,
                      // Relative bet counts, never currency (§2.3).
                      display: count(market.weight),
                      tip: `${humanise(market.market)} · ${count(market.weight)} bets on ${humanise(team.team)}`,
                    }))}
                    max={maxWeight}
                    height={6}
                    colorClass="bg-chart-sports"
                    labelWidth={82}
                    valueWidth={20}
                  />
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="space-y-3 border-t pt-3">
        <ChipRow label="Top leagues" items={summary?.top_5_leagues} />
        <ChipRow label="Top markets" items={summary?.top_5_markets} />
        {/* top_5_players is empty in the sample — the row is hidden, not shown blank. */}
        <ChipRow label="Top players" items={summary?.top_5_players} />
      </div>
    </div>
  );
}

export function SportsDna({ profile, loading }) {
  const favourites = useMemo(() => deriveFavourites(profile?.sports?.by_sport), [profile]);
  const active = useMemo(() => hasSportsActivity(profile), [profile]);
  // Collapse only for an established player who never bets sports; a brand-new
  // player keeps all four cards and shows the sparse empty state in each (§7.3).
  const collapse = !active && hasCasinoProfile(profile);

  if (loading) {
    return (
      <section>
        <SectionHeading id="pi-sports" eyebrow="Section D" title="Sports DNA" />
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          {[0, 1, 2].map((index) => (
            <div key={index} className="rounded-lg border bg-card p-4 xl:col-span-4">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="mt-4 h-36 w-full rounded-md" />
            </div>
          ))}
          <div className="rounded-lg border bg-card p-4 xl:col-span-12">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="mt-4 h-32 w-full rounded-md" />
          </div>
        </div>
      </section>
    );
  }

  // §7.4 — no sportsbook activity collapses the whole section to one quiet card
  // rather than leaving four empty ones.
  if (collapse) {
    return (
      <section>
        <SectionHeading id="pi-sports" eyebrow="Section D" title="Sports DNA" />
        <InsightCard>
          <div className="flex items-center gap-3 py-2">
            <Shapes className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div>
              <p className="text-sm text-foreground">No sportsbook activity.</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                This player has only ever played casino games.
              </p>
            </div>
          </div>
        </InsightCard>
      </section>
    );
  }

  return (
    <section>
      <SectionHeading
        id="pi-sports"
        eyebrow="Section D"
        title="Sports DNA"
        description="Odds appetite, accumulator habits and who they follow."
      />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-12">
        <InsightCard className="xl:col-span-4" title="Odds appetite" subtitle="Share of bets by odds range">
          <OddsAppetite ranges={profile?.sports?.preferred_odds_ranges} />
        </InsightCard>

        <InsightCard className="xl:col-span-4" title="Bet types" subtitle="Share of bets by number of legs">
          <BetTypes types={profile?.sports?.preferred_bet_types} />
        </InsightCard>

        <InsightCard className="md:col-span-2 xl:col-span-4" title="Stake mix" subtitle="Share of sports bets by stake size">
          <StakeMix stakes={profile?.sports?.preferred_stakes_kes} colorClass="bg-chart-sports" />
        </InsightCard>

        <InsightCard
          className="md:col-span-2 xl:col-span-12"
          title="Favourites"
          subtitle="Bet counts by team and market"
        >
          <Favourites favourites={favourites} summary={profile?.sports?.summary} />
        </InsightCard>
      </div>
    </section>
  );
}
